import { cx } from '../ui/cx.ts';

/**
 * Horizontal labeled meter row — Dispatch Deck chart quartet (epic
 * #897). Replaces the hand-rolled health-meter rows formerly on the
 * Dashboard (the Usage service-health panel may reuse it in epic PR 6).
 *
 * Accessibility: the track carries `role="meter"` with
 * `aria-valuemin/max/now` and the row label as its accessible name.
 * The fill bar itself is decoration — the label (left) and the mono
 * value text (right) ARE the text alternative, so the number is always
 * readable without the visual.
 *
 * Edges: `max <= 0` (or non-finite inputs) renders an empty track with
 * `aria-valuenow` 0; `value > max` clamps to a full track.
 */

export type MeterTone = 'ok' | 'warn' | 'bad' | 'neutral';

const FILL_TONE: Record<MeterTone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  neutral: 'bg-paper-low',
};

interface Props {
  /** Row label (already translated). */
  readonly label: string;
  readonly value: number;
  readonly max: number;
  readonly tone?: MeterTone;
  /** Display string for the right column; defaults to `String(value)`. */
  readonly valueText?: string;
  readonly testId?: string;
}

export function Meter({
  label,
  value,
  max,
  tone = 'neutral',
  valueText,
  testId,
}: Props): JSX.Element {
  const safeValue = Number.isFinite(value) ? Math.max(0, value) : 0;
  const safeMax = Number.isFinite(max) ? Math.max(0, max) : 0;
  const ratio = safeMax > 0 ? Math.min(1, safeValue / safeMax) : 0;
  return (
    <div
      data-testid={testId}
      className="grid grid-cols-[minmax(4rem,auto)_minmax(0,1fr)_minmax(4rem,auto)] items-center gap-3"
    >
      <span className="truncate text-label text-paper-mid">{label}</span>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-valuenow={Math.min(safeValue, safeMax)}
        className="h-2 overflow-hidden rounded-sm bg-well"
      >
        <div
          className={cx('h-full rounded-sm', FILL_TONE[tone])}
          style={{ width: `${(ratio * 100).toFixed(2)}%` }}
        />
      </div>
      <span className="text-right font-mono text-label text-paper">
        {valueText ?? String(safeValue)}
      </span>
    </div>
  );
}
